import {
  AfterViewInit,
  Component,
  inject,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { KeywordQuestionAnswer } from '../../../../models/answer/keyword-answer.model';
import { KeywordQuestion } from '../../../../models/questions/keyword-question.model';
import { OnLoaded } from '@lyceo/shared/dynamic-component-loading/feature-main';
import { QuestionControlType } from '../question-type.interface';
import { QuestionDisplayComponent } from '../../question-display/question-display.component';
import { IonTextarea } from '@ionic/angular/standalone';

/**
 * The question type that contains a textarea. The height of the textarea
 * will increase based on the value.
 *
 * <lib-keyword-question
 *                    [formControl]="questionFormCtrl">
 * </lib-keyword-question>
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-keyword-question',
  templateUrl: './keyword-question.component.html',
  styleUrls: ['./keyword-question.component.scss'],
  imports: [QuestionDisplayComponent, IonTextarea, ReactiveFormsModule],
})
export class KeywordQuestionComponent
  implements AfterViewInit, OnLoaded, QuestionControlType
{
  private readonly document: Document = inject<Document>(DOCUMENT);

  /**
   * The question that is presented.
   */
  public question!: KeywordQuestion;

  /**
   * Input to prefill the answer of the question.
   */
  public set answer(answer: KeywordQuestionAnswer | undefined) {
    this.textareaCtrl.setValue(answer);
  }

  public get answer(): KeywordQuestionAnswer | undefined {
    return this.textareaCtrl.value;
  }

  /**
   * The form control ion-range component
   */
  public textareaCtrl: UntypedFormControl = new UntypedFormControl(
    null,
    Validators.required,
  );

  /**
   * Subject which starts to emit when the view is rendered. This is needed because the
   * component is dynamically loading.
   */
  public onFinishedInit$: ReplaySubject<void> = new ReplaySubject<void>();

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    this.onFinishedInit$.next();
    this.onFinishedInit$.complete();
  }

  /**
   * @inheritDoc
   */
  public ready(): Promise<void> {
    return firstValueFrom(this.onFinishedInit$.pipe(delay(20)));
  }

  /**
   * @inheritDoc
   */
  public isPristine(): boolean {
    return this.textareaCtrl.pristine;
  }

  /**
   * @inheritDoc
   */
  public getValue(): KeywordQuestionAnswer {
    const value: string = this.textareaCtrl.value;
    if (typeof value === 'string') {
      return value.trim();
    }
    return null as never;
  }

  /**
   * @inheritDoc
   */
  public onValueChanges(): Observable<KeywordQuestionAnswer> {
    return this.textareaCtrl.valueChanges.pipe(
      map((value) => {
        if (typeof value === 'string') {
          return value.trim();
        }
        return null as never;
      }),
    );
  }

  /**
   * @inheritDoc
   */
  public isValid(): boolean {
    return this.textareaCtrl.valid;
  }

  /**
   * @inheritDoc
   */
  public onValidChanges(): Observable<boolean> {
    return this.textareaCtrl.statusChanges.pipe(
      filter((status) => status === 'VALID' || status === 'INVALID'),
      map((status) => status === 'VALID'),
    );
  }

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.textareaCtrl.disable();
    } else {
      this.textareaCtrl.enable();
    }
  }

  public getFocusElement(): Element | null {
    return this.document.querySelector('lib-keyword-question textarea');
  }
}
